import { Button, Stack, Text, TextInput } from '@mantine/core';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSearchableOptions } from '../../hooks/use-searchable-options';
import { FilterOption, toVirtualCheckboxItems } from '../../utils/filter-options';
import {
  isAllOrganisationsSelected,
  isNoOrganisationSelected,
  getEffectiveOrganisationSelectedIds,
  toggleOrganisationInSelection,
} from '../../utils/organisation-id-set-filter';

import { FilterPopoverShell } from './filter-popover-shell';
import { VirtualCheckboxList } from './virtual-checkbox-list';

interface OwnerFilterPopoverProps {
  ownerIds: number[];
  ownerOptions: FilterOption[];
  onOwnerIdsChange: (ids: number[]) => void;
}

export const OwnerFilterPopover: FC<OwnerFilterPopoverProps> = ({
  ownerIds,
  ownerOptions,
  onOwnerIdsChange,
}) => {
  const { t } = useTranslation();
  const {
    debouncedSearch,
    filteredOptions,
    isSearchPending,
    resetSearch,
    search,
    setSearch,
  } = useSearchableOptions(ownerOptions);

  const allOwnerIds = useMemo(
    () => ownerOptions.map((owner) => owner.id),
    [ownerOptions],
  );

  const isAllSelected = isAllOrganisationsSelected(ownerIds, allOwnerIds);
  const isNoneSelected = isNoOrganisationSelected(ownerIds);
  const effectiveSelectedIds = useMemo(
    () => getEffectiveOrganisationSelectedIds(ownerIds),
    [ownerIds],
  );

  const listItems = useMemo(
    () => toVirtualCheckboxItems(filteredOptions),
    [filteredOptions],
  );

  const badgeLabel = isAllSelected
    ? t('statistics.filter-all')
    : String(ownerIds.length);

  const toggleOwner = useCallback(
    (ownerId: number) => {
      onOwnerIdsChange(toggleOrganisationInSelection(ownerIds, ownerId));
    },
    [onOwnerIdsChange, ownerIds],
  );

  return (
    <FilterPopoverShell
      badgeLabel={badgeLabel}
      headerActions={
        <>
          <Button
            disabled={isAllSelected}
            onClick={() => onOwnerIdsChange([...allOwnerIds])}
            size="compact-xs"
            variant="subtle"
          >
            {t('statistics.filter-select-all')}
          </Button>
          <Button
            disabled={isNoneSelected}
            onClick={() => onOwnerIdsChange([])}
            size="compact-xs"
            variant="subtle"
          >
            {t('statistics.filter-deselect-all')}
          </Button>
        </>
      }
      isAllSelected={isAllSelected}
      label={t('statistics.owner-filter')}
      onClose={resetSearch}
      title={t('statistics.search-owner')}
      width={360}
    >
      <TextInput
        autoFocus
        onChange={(event) => setSearch(event.currentTarget.value)}
        placeholder={t('statistics.search-owner')}
        value={search}
      />
      {ownerOptions.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t('statistics.owner-filter-empty')}
        </Text>
      ) : isSearchPending ? (
        <Text c="dimmed" size="sm">
          {t('statistics.owner-filter-searching')}
        </Text>
      ) : filteredOptions.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t('statistics.owner-filter-no-results')}
        </Text>
      ) : (
        <Stack gap={4}>
          <Text c="dimmed" size="xs">
            {t('statistics.owner-filter-count', {
              count: filteredOptions.length,
            })}
          </Text>
          <VirtualCheckboxList
            key={debouncedSearch}
            items={listItems}
            onToggle={toggleOwner}
            selectedIds={effectiveSelectedIds}
          />
        </Stack>
      )}
    </FilterPopoverShell>
  );
};

export default OwnerFilterPopover;
